/*
 * product-group-selector.js — `<product-group-selector-component>`
 * custom element. Two responsibilities:
 *
 *   1. Hover label swap. The `.product-group-selector__name`
 *      span shows the current product's colour name by
 *      default; hovering a swatch / pill swaps it to that
 *      sibling's name, reverts on mouse-leave. Mirrors
 *      v3.0's product-group-selector.liquid behaviour.
 *
 *   2. Dropdown navigate. When the block is in `dropdown`
 *      mode it embeds a `<custom-select-component>` whose
 *      values are sibling product URLs. Listen for the
 *      custom-select's `change` event (it bubbles via the
 *      inner hidden input) and navigate to the picked URL.
 *
 * Loaded as `type="module"` from snippets/scripts.liquid
 * inside the product-template gate. Self-init: each
 * `<product-group-selector-component>` element runs its own
 * connectedCallback when the upgrade lands.
 */

class ProductGroupSelectorComponent extends HTMLElement {
  connectedCallback() {
    this._bindHoverLabel();
    this._bindDropdownNavigate();
  }

  _bindHoverLabel() {
    const nameEl = this.querySelector('[data-current-name]');
    if (!nameEl) return;

    // Capture the original name once so mouse-leave can restore
    // it cleanly even after multiple hovers in a row.
    const originalName = nameEl.textContent;

    // Scope to elements inside the component carrying the
    // namespaced `data-pgs-name` attribute. The prefix matches
    // the `--pgs-*` CSS vars on the block root, so the attr
    // is unambiguously owned by this component — no risk of
    // colliding with `data-name` attributes any other block /
    // section / theme component might emit.
    const items = this.querySelectorAll('[data-pgs-name]');
    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        nameEl.textContent = item.dataset.pgsName || originalName;
      });
      item.addEventListener('mouseleave', () => {
        nameEl.textContent = originalName;
      });
    });
  }

  _bindDropdownNavigate() {
    if (this.dataset.pickerType !== 'dropdown') return;

    // custom-select renders a `<custom-select-component>` host
    // with an inner hidden `<input>` carrying the selected
    // value. `change` events from that input bubble up the
    // host. The value we set per option in Liquid is the
    // sibling's URL, so navigating is a one-liner.
    const select = this.querySelector('custom-select-component');
    if (!select) return;

    select.addEventListener('change', (event) => {
      const target = event.target instanceof HTMLInputElement
        ? event.target
        : select.querySelector('input[type="hidden"]');
      const url = target?.value;
      if (url) window.location.href = url;
    });
  }
}

if (!customElements.get('product-group-selector-component')) {
  customElements.define('product-group-selector-component', ProductGroupSelectorComponent);
}
